import React from 'react';
import { useIntl } from 'react-intl';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons/faExclamationTriangle';

import { FlexColumn } from 'ui-components/components/flex/column';
import { FlexRow, Gap } from 'ui-components/components/flex';
import { Text, TextType } from 'ui-components/components/text';

import { messages } from '../../messages';
import style from './delete-blocked-content.module.css';
import type { ProductUnitOfMeasureResponse } from '../../uom-types';

export type DeleteBlockedInfo = {
  row: ProductUnitOfMeasureResponse;
  reason: string;
  childNames: string[];
};

type DeleteBlockedContentProps = {
  deleteBlockedInfo: DeleteBlockedInfo;
  allItemsById: Map<number, ProductUnitOfMeasureResponse>;
};

export const DeleteBlockedContent = ({ deleteBlockedInfo, allItemsById }: DeleteBlockedContentProps) => {
  const intl = useIntl();
  const { row, reason, childNames } = deleteBlockedInfo;
  const parent = row.parentProductUnitOfMeasureId != null ? allItemsById.get(row.parentProductUnitOfMeasureId) : undefined;
  const parentName = parent?.uomLevel ?? parent?.uomTypeName ?? '';

  const subtitle =
    childNames.length > 0
      ? intl.formatMessage(messages.uomDeleteBlockedSubtitleChain, {
          unitName: row.uomTypeName,
          quantity: row.conversionFactor,
          parentName,
        })
      : `${row.uomTypeName} · ${row.conversionFactor} ${parentName}`.trim();

  return (
    <FlexRow className={style['delete-blocked-row']} gap={Gap.Space12}>
      <FontAwesomeIcon className={style['delete-blocked-icon']} icon={faExclamationTriangle} />
      <FlexColumn className={style['delete-blocked-content']} gap={Gap.Space4}>
        <Text element='div' textType={TextType.Label} value={subtitle} />
        <Text element='div' textType={TextType.Caption} value={reason} />
        {childNames.length > 0 && (
          <FlexRow className={style['delete-blocked-tags']} gap={Gap.Space8}>
            <Text element='span' textType={TextType.Caption} value={messages.uomDeleteBlockedUsedBy} />
            {childNames.map((name) => (
              <Text key={name} className={style['delete-blocked-tag']} element='span' textType={TextType.Caption} value={name} />
            ))}
          </FlexRow>
        )}
      </FlexColumn>
    </FlexRow>
  );
};
